import type { GetApiUserListParams, UserDTO } from '@smartsash/api-client';
import { isRoleName, ROLE_HIERARCHY, ROLES } from '@smartsash/permissions';
import type { z } from 'zod';

import { ADMIN_LIST_SEARCH_DEFAULTS, createAdminListSearchSchema } from '@/router/listSearch';
import { firstNonEmpty } from '@/utils/firstNonEmpty';
import {
  buildFilterParams,
  collectSearchTerms,
  sortParams,
  type FilterParamMap,
} from '@/utils/listQueryParams';

export const usersSearchSchema = createAdminListSearchSchema();

export type UsersSearch = z.infer<typeof usersSearchSchema>;

/**
 * The builder fields `GET /api/User/list` receives.
 *
 * The page reads the successor route rather than `GET /api/User`, which has no search, no sort and
 * no `role`, and whose `labId` is typed `number` while a laboratory id is a string GUID — a filter
 * that cannot be called with a real value. On the new route `labId` is a GUID and, like `groupId`,
 * matches on the user's active assignments rather than on a foreign key.
 */
export const USERS_INSTITUTION_FIELD = 'institution';
export const USERS_CERTIFICATION_FIELD = 'certification';
export const USERS_ROLE_FIELD = 'role';
export const USERS_GROUP_FIELD = 'groupId';
export const USERS_LAB_FIELD = 'labId';
export const USERS_ENABLED_FIELD = 'isEnabled';
export const USERS_ACTIVE_UNTIL_FIELD = 'deactivationDate';

/**
 * The roles the endpoint filters on, highest first.
 *
 * `role` matches a user who holds any of the given roles by name, and the generated client has no
 * enum to read the vocabulary from — so it is `ROLE_HIERARCHY`'s six roles, highest first. The
 * authentication marker roles (`SashDevice`, `Authenticated`, `FunctionAuthorization`) are
 * deliberately absent because they are never a user's role.
 *
 * Reuses `@smartsash/permissions`'s `ROLE_HIERARCHY` (HS-1384) rather
 * than a second hardcoded list — a prior copy here drifted to stale spellings
 * (`HawkinsAdmin`/`LabAdmin`/`SashUser`, pre-HS-1384) undetected by `typecheck` because the
 * literal array carried no relationship to `RoleName`.
 */
export const USERS_ROLES = ROLE_HIERARCHY;

/**
 * Column id → the endpoint's `sortBy` value.
 *
 * The whitelist is `lastName`, `email`, `activeUntil`, `isCertified` and `createdAt`. The name
 * column sorts by `lastName`, which is what the server orders on and what the endpoint defaults to.
 */
export const USERS_SORT_KEYS: Readonly<Record<string, string>> = {
  name: 'lastName',
  email: 'email',
  certification: 'isCertified',
  deactivationDate: 'activeUntil',
};

export const USERS_FILTER_PARAMS: FilterParamMap<GetApiUserListParams> = {
  [USERS_INSTITUTION_FIELD]: { kind: 'multi', param: 'institutionId' },
  [USERS_GROUP_FIELD]: { kind: 'multi', param: 'groupId' },
  [USERS_LAB_FIELD]: { kind: 'multi', param: 'labId' },
  [USERS_ROLE_FIELD]: { kind: 'multi', param: 'role' },
  [USERS_CERTIFICATION_FIELD]: { kind: 'boolean', param: 'certified' },
  [USERS_ENABLED_FIELD]: { kind: 'boolean', param: 'isEnabled' },
  [USERS_ACTIVE_UNTIL_FIELD]: { kind: 'dateRange', from: 'activeUntilFrom', to: 'activeUntilTo' },
};

/**
 * Turn the URL's `page`/`q`/`sort`/`filters` into `GetApiUserListParams` (HS-869 follow-up). Shared
 * by the screen's own `query`, `AddUserButton`'s tenant lookup and `UsersFilter`'s cache read, which
 * all have to build the exact same params or they would read different cache entries.
 */
export function usersQueryParams(search: UsersSearch): GetApiUserListParams {
  const conditions = search.filters ?? [];
  const searchTerms = collectSearchTerms(conditions, search.q ?? '');
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...sortParams<GetApiUserListParams>(search.sort, USERS_SORT_KEYS),
    ...(searchTerms ? { search: searchTerms } : {}),
    ...buildFilterParams(conditions, USERS_FILTER_PARAMS),
  };
}

/** The composite key every user route and mutation is addressed by. */
export function userKeyOf(row: UserDTO) {
  return { id: row.id ?? '', tenantId: row.institutionId ?? '' };
}

/** The name the list shows: full name, falling back to the e-mail and then the key. */
export function userDisplayName(row: UserDTO) {
  return firstNonEmpty([row.firstName, row.lastName].filter(Boolean).join(' '), row.email, row.id);
}

/**
 * The text each column holds for a row (HS-869). No table `meta` here: `UserDTO` embeds its
 * institution, so nothing needs a joined lookup.
 *
 * `certification` is the one reader that needs translated copy — its text is the badge's label — so
 * it takes a translator rather than closing over one.
 */
export const usersColumnText = {
  name: (row: UserDTO) => `${userDisplayName(row)} ${row.userIdentifier ?? ''}`.trim(),
  institution: (row: UserDTO) => row.institution?.name ?? row.institutionId ?? '',
  // HS-1565 slice 2: the base role's label, empty for none or an unknown name — the same
  // whitelist as `assignableRoleLabel`, written out because this module sits in the initial
  // bundle and importing `utils/roleLabel` here pulled that module in as an extra preloaded chunk.
  role: (row: UserDTO) =>
    row.baseRole != null && isRoleName(row.baseRole) ? ROLES[row.baseRole].label : '',
  email: (row: UserDTO) => row.email ?? '',
  certification: (row: UserDTO, translate: (key: 'certified' | 'uncertified') => string) =>
    row.isCertified === true ? translate('certified') : translate('uncertified'),
  deactivationDate: (row: UserDTO) => row.activeUntil ?? '',
} as const;

export type UsersColumnId = keyof typeof usersColumnText;

/**
 * The search that links an institution, a group or a laboratory to its users (HS-869, HS-1240).
 *
 * The old links carried `?instituteId=`/`?institutionId=`, which this list never read.
 * `GET /api/User/list` accepts all three ids, and the builder already routes them through
 * `filters`, so the link applies the condition it always claimed to.
 *
 * `labId` and `groupId` match on the user's **active assignments**, not on a foreign key, which is
 * exactly what "the users who can work in this lab" means — so "View users" answers the question
 * the row action asks rather than opening the full list (HS-1240).
 */
export function usersFilteredBy(
  field: typeof USERS_INSTITUTION_FIELD | typeof USERS_GROUP_FIELD | typeof USERS_LAB_FIELD,
  value: string,
) {
  return {
    ...ADMIN_LIST_SEARCH_DEFAULTS,
    filters: [{ field, operator: 'eq' as const, values: [value] }],
  };
}

export function usersFilteredByInstitution(institutionId: string) {
  return usersFilteredBy(USERS_INSTITUTION_FIELD, institutionId);
}

/**
 * The search that links a laboratory to its users.
 *
 * `labId` used to be believed unusable because `GetApiUserParams` (`GET /api/User`) types it as a
 * `number` against GUID lab ids. `GET /api/User/list` — this page's real source, see
 * `usersQueryParams` — types it as a GUID `string[]`, so the condition reaches the server.
 */
export function usersFilteredByLab(labId: string) {
  return usersFilteredBy(USERS_LAB_FIELD, labId);
}

/**
 * The search that links a group to its users.
 *
 * `GroupDetails`'s "View users" used to open the unfiltered list under the same `GetApiUserParams`
 * belief as `usersFilteredByLab` — `GET /api/User/list` accepts `groupId` as a GUID `string[]` too.
 */
export function usersFilteredByGroup(groupId: string) {
  return usersFilteredBy(USERS_GROUP_FIELD, groupId);
}
